import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { ICompany } from 'v2/modules/client/interfaces/client-company.interface';
import { CountryConfig } from 'v2/modules/shared/configs/country.config';
import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';
import { OptionInterface } from 'v2/modules/shared/interfaces/common.interface';
import { ClientApiService } from 'v2/modules/shared/services/api/client/client-api.service';
import { ClientCompanyApiService } from 'v2/modules/shared/services/api/client/client-company-api.service';

@Component({
  selector: 'lum-client-profile-business-address-form',
  templateUrl: './client-profile-business-address-form.component.html',
  styleUrls: ['./client-profile-business-address-form.component.scss'],
})
export class ClientProfileBusinessAddressFormComponent {
  @Input()
  form :UntypedFormGroup;

  @Output()
  tabChange = new EventEmitter<{ name :string; index :number }>();

  @Output()
  tabValidation = new EventEmitter<{ invalid :boolean; nextTab :string }>();

  countries = CountryConfig.countries;
  companies :ICompany[] = [];
  companies$ :Observable<ICompany[]>;
  countryList :OptionInterface[] = [];
  loading$ :Observable<boolean>;
  record$ :Observable<any>;

  private onDestroy$ = new Subject();

  constructor(
    private snackBar :MatSnackBar,
    private clientApiService :ClientApiService,
    private clientCompanyApiService :ClientCompanyApiService,
  ) {}

  ngOnInit() :void {
    this.getDataFromService();
    this.getCompanies().subscribe();
    this.formatCountryList();
  }
  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onChangeTab(direction :string) :void {
    if (direction == 'previous') {
      this.onSetTab('client-info', 0);
    } else {
      this.onSetTab('shipping-address', 2);
    }
  }
  onSetTab(name :string, index :number) :void {
    this.tabChange.emit({
      name,
      index,
    });
  }
  onSubmit() :void {
    if (!this.hasInvalidInput) {
      const clientId = this.form?.get('business_account_id')?.value;
      if (clientId) {
        this.clientApiService
          .updateProfile(clientId, this.form.value)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((response) => {
            const currentCompany = response.record;
            const companyIndex = this.companies.findIndex(
              (company :ICompany) => company.id === currentCompany.id,
            );
            this.companies[companyIndex] = currentCompany;
            this.clientCompanyApiService.setCurrentCompany(currentCompany);
            this.clientCompanyApiService.setCompanies(this.companies);
            this.onResponse(response);
          });
      } else {
        this.clientApiService
          .createProfile(this.form.value)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((response) => {
            this.onResponse(response);
          });
      }
    }
  }

  private getDataFromService() :void {
    this.loading$ = this.clientApiService.loading$;
    this.record$ = this.clientApiService.clients$;
    this.companies$ = this.clientCompanyApiService.companies$;
  }
  private getCompanies() :Observable<ICompany[]> {
    return this.companies$.pipe(
      tap((companies) => {
        if (companies) {
          this.companies = companies;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }
  private formatCountryList() :void {
    this.countryList = Object.keys(this.countries).map((index :string) => ({
      value: index,
      label: this.countries[index as keyof typeof this.countries],
    }));
  }
  private onResponse(response :any) :void {
    this.snackBar.open(response.message, 'Success', {
      duration: SnackBarConfig.DEFAULT.duration,
    });
    this.onChangeTab('next');
  }

  get country() :OptionInterface | null {
    const code = this.form?.get('business_country')?.value;
    const index = this.countryList.findIndex((t) => t.value === code);

    return this.countryList[index] || null;
  }
  get hasInvalidInput() :boolean {
    const invalid =
      this.form?.controls['business_address']?.invalid ||
      this.form?.controls['business_state']?.invalid ||
      this.form?.controls['business_city']?.invalid ||
      this.form?.controls['business_zip']?.invalid ||
      this.form?.controls['business_country']?.invalid ||
      this.form?.controls['phone']?.invalid;
    this.tabValidation.emit({
      invalid,
      nextTab: 'shipping-address',
    });

    return invalid;
  }
  get businessAddressControl() :UntypedFormControl {
    return this.form.get('business_address') as UntypedFormControl;
  }
  get businessStateControl() :UntypedFormControl {
    return this.form.get('business_state') as UntypedFormControl;
  }
  get businessCityControl() :UntypedFormControl {
    return this.form.get('business_city') as UntypedFormControl;
  }
  get businessZipControl() :UntypedFormControl {
    return this.form.get('business_zip') as UntypedFormControl;
  }
  get businessCountryControl() :UntypedFormControl {
    return this.form.get('business_country') as UntypedFormControl;
  }
  get phoneControl() :UntypedFormControl {
    return this.form.get('phone') as UntypedFormControl;
  }
}
